// Save/remove browser Push subscriptions and manage user devices.
// VAPID_PRIVATE_KEY is never exposed to the client.

import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";
import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";

const Sub = z.object({
  endpoint: z.string().url(),
  p256dh: z.string().min(10),
  auth: z.string().min(10),
  user_agent: z.string().max(500).nullish(),
  label: z.string().max(80).nullish(),
});

export const savePushSubscription = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d: unknown) => Sub.parse(d))
  .handler(async ({ data, context }) => {
    const { error } = await context.supabase.from("push_subscriptions").upsert(
      {
        user_id: context.userId,
        endpoint: data.endpoint,
        p256dh: data.p256dh,
        auth: data.auth,
        user_agent: data.user_agent ?? null,
        label: data.label ?? null,
        last_seen_at: new Date().toISOString(),
        failure_count: 0,
        disabled_at: null,
        last_error: null,
      },
      { onConflict: "user_id,endpoint" },
    );
    if (error) throw new Error(error.message);
    return { ok: true };
  });

export const removePushSubscription = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d: unknown) => z.object({ endpoint: z.string().url().optional(), id: z.string().uuid().optional() }).refine(v => v.endpoint || v.id, "endpoint or id required").parse(d))
  .handler(async ({ data, context }) => {
    let q = context.supabase.from("push_subscriptions").delete().eq("user_id", context.userId);
    if (data.id) q = q.eq("id", data.id);
    if (data.endpoint) q = q.eq("endpoint", data.endpoint);
    const { error } = await q;
    if (error) throw new Error(error.message);
    return { ok: true };
  });

export const listMyPushDevices = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    const { data } = await context.supabase
      .from("push_subscriptions")
      .select("id, endpoint, label, user_agent, failure_count, last_success_at, last_failure_at, disabled_at, created_at, last_seen_at")
      .eq("user_id", context.userId)
      .order("created_at", { ascending: false });
    // Redact endpoint (contains provider-specific token) — return only first 40 chars.
    return (data ?? []).map((d) => ({ ...d, endpoint_preview: (d.endpoint ?? "").slice(0, 40) + "…", endpoint: undefined }));
  });

export const getVapidPublicKey = createServerFn({ method: "GET" }).handler(async () => {
  return { key: process.env.VAPID_PUBLIC_KEY ?? null };
});
